/**
 * useGameSlate — Date navigation + game filtering for the value dashboard.
 *
 * Manages selectedDate, gameFilter state, available dates, date auto-snap,
 * and filtered game lists for both moneyline and current-sport views.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { getTodayStr, findNearestAvailableDate, isGameLikelyOver } from '@/lib/infra/dateUtils';
import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';
import type { GameData } from '@/lib/sportsStream';
import type { Sport } from '@/lib/sportsDiscovery';

export type GameFilterMode = 'active' | 'all' | 'settled';

interface UseGameSlateParams {
  /** Moneyline games (enriched with rotation numbers) */
  moneylineGames: ConsolidatedGameBooks[];
  /** Current sport/market games (spread, total, or non-NBA moneyline) */
  currentGames: GameData[];
  /** Whether moneyline data is still loading */
  moneylineLoading: boolean;
  /** Whether current sport data is still loading */
  currentLoading: boolean;
  /** Currently selected sport (for isGameLikelyOver duration) */
  selectedSport: Sport;
}

export interface GameSlateResult {
  selectedDate: string | null;
  setSelectedDate: (date: string | null) => void;
  gameFilter: GameFilterMode;
  setGameFilter: (filter: GameFilterMode) => void;
  availableDates: string[];
  moneylineGamesForView: ConsolidatedGameBooks[];
  filteredCurrentGames: GameData[];
  handleDatePrev: () => void;
  handleDateNext: () => void;
  handleDateToday: () => void;
  handleDateAll: () => void;
}

export function useGameSlate({
  moneylineGames,
  currentGames,
  moneylineLoading,
  currentLoading,
  selectedSport,
}: UseGameSlateParams): GameSlateResult {
  const [selectedDate, setSelectedDate] = useState<string | null>(() => getTodayStr());
  const [gameFilter, setGameFilter] = useState<GameFilterMode>('active');

  // Collect unique sorted dates from ALL games
  const availableDates = useMemo(() => {
    const dateSet = new Set<string>();
    for (const g of moneylineGames) if (g.date) dateSet.add(g.date);
    for (const g of currentGames) if (g.date) dateSet.add(g.date);
    return Array.from(dateSet).sort();
  }, [moneylineGames, currentGames]);

  // Auto-snap: if selected date has no games, jump to nearest available date.
  // Guard: don't snap while data is still loading — partial availableDates can mis-snap.
  useEffect(() => {
    if (moneylineLoading || currentLoading) return;
    if (!selectedDate || availableDates.length === 0) return;
    const snapped = findNearestAvailableDate(selectedDate, availableDates);
    if (snapped !== null) setSelectedDate(snapped);
  }, [selectedDate, availableDates, moneylineLoading, currentLoading]);

  // Filter moneyline games by selected date + completion status
  const moneylineGamesForView = useMemo(() => {
    let games = moneylineGames;
    if (selectedDate) {
      games = games.filter((g) => g.date === selectedDate);
    }
    if (gameFilter === 'active') {
      games = games.filter((g) => !isGameLikelyOver(g.date, g.startTimePt, selectedSport));
    } else if (gameFilter === 'settled') {
      games = games.filter((g) => isGameLikelyOver(g.date, g.startTimePt, selectedSport));
    }
    return games;
  }, [moneylineGames, selectedDate, gameFilter, selectedSport]);

  // Filter current sport games by selected date + completion status
  const filteredCurrentGames = useMemo(() => {
    let games = currentGames;
    if (selectedDate) {
      games = games.filter((g) => g.date === selectedDate);
    }
    if (gameFilter === 'active') {
      games = games.filter((g) => !isGameLikelyOver(g.date, g.startTimePt, selectedSport));
    } else if (gameFilter === 'settled') {
      games = games.filter((g) => isGameLikelyOver(g.date, g.startTimePt, selectedSport));
    }
    return games;
  }, [currentGames, selectedDate, gameFilter, selectedSport]);

  // Date navigation
  const handleDatePrev = useCallback(() => {
    if (!selectedDate || availableDates.length === 0) return;
    const idx = availableDates.indexOf(selectedDate);
    if (idx > 0) setSelectedDate(availableDates[idx - 1]!);
  }, [selectedDate, availableDates]);

  const handleDateNext = useCallback(() => {
    if (!selectedDate || availableDates.length === 0) return;
    const idx = availableDates.indexOf(selectedDate);
    if (idx < availableDates.length - 1) setSelectedDate(availableDates[idx + 1]!);
  }, [selectedDate, availableDates]);

  const handleDateToday = useCallback(() => {
    setSelectedDate(getTodayStr());
  }, []);

  const handleDateAll = useCallback(() => {
    setSelectedDate(null);
  }, []);

  return {
    selectedDate,
    setSelectedDate,
    gameFilter,
    setGameFilter,
    availableDates,
    moneylineGamesForView,
    filteredCurrentGames,
    handleDatePrev,
    handleDateNext,
    handleDateToday,
    handleDateAll,
  };
}
